import type { DateRangePreset } from "@@/backoffice/for-providers/reports/transactions/transaction-date-presets";
import { currentTimezoneName } from "@@/shared/current-timezone-name";
import { Form } from "@@/shared/form/form.zustand-store";
import { usePrevious } from "@@/shared/use-previous";
import type {
    DateRange,
    Translatable,
    TranslatableValueFactoryParams} from "@towni/common";
import {
    dateRangeFactory,
    dateRangeUtils,
    dateRangeZodObject,
    endOfMonthZoned,
    generateId,
    isDateRange,
    startOfMonthZoned,
} from "@towni/common";
import type { ReactNode} from "react";
import { useEffect, useState } from "react";
import { z } from "zod";
import type { FormId } from "../../form-and-field-id";
import { useFormFieldActions } from "../../use-form-field";

const defaultFrom = () => startOfMonthZoned(new Date(), currentTimezoneName);
const defaultTo = () => endOfMonthZoned(new Date(), currentTimezoneName);

type Props = {
    readonly initialDateRange?: DateRange;
    readonly onChange: (dateRange: DateRange) => void;
    readonly presets?: DateRangePreset[];
    readonly children?: ReactNode;
    readonly label?: Translatable | TranslatableValueFactoryParams;
    readonly showWeekdayNames?: boolean;
    readonly showPresetTitle?: boolean;
    readonly hideRequiredStar?: boolean;
    readonly className?: string;
    readonly value?: DateRange;
};

type FormState = { dateRange: DateRange };
const DateRangePicker = (props: Props) => {
    const [formId] = useState<FormId>(() => generateId());
    const actions = useFormFieldActions<FormState>(formId);

    const previousValue = usePrevious(dateRangeUtils.toString(props.value));
    useEffect(() => {
        const currentValue = dateRangeUtils.toString(props.value);
        // If the value is undefined, we don't care
        if (currentValue === undefined) return;
        // If the value hasn't changed, we don't need to do anything
        if (previousValue === currentValue) return;
        // If the value has changed, we need to update the form
        actions?.setFieldValue(draft => {
            draft.dateRange = props.value;
        });
    }, [props.value, previousValue]);

    if (!actions) return null;
    return (
        <Form<FormState>
            formId={formId}
            clearOnUnmount
            initialization={{
                formSchema: z.object({
                    dateRange: dateRangeZodObject,
                }),
                initialState: {
                    dateRange:
                        props.initialDateRange ??
                        dateRangeFactory.from({
                            start: defaultFrom(),
                            end: defaultTo(),
                        }),
                },
            }}>
            {({ FormComponents }) => (
                <FormComponents.DateRangePicker
                    fieldId="dateRange"
                    label={props.label}
                    className={props.className}
                    customPresets={props.presets}
                    showPresetTitle={props.showPresetTitle}
                    showWeekdayNames={props.showWeekdayNames}
                    hideRequiredStar={props.hideRequiredStar}
                    getter={data => data.dateRange}
                    setter={(data, newValue) => {
                        if (!isDateRange(newValue)) return;
                        data.dateRange = newValue;
                        props.onChange(newValue);
                    }}
                />
            )}
        </Form>
    );
};

export { DateRangePicker };
